import { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Typography, Box, CircularProgress, Alert,
} from '@mui/material';

/**
 * A referrer's own /r/{code} link, for the agent to pass on.
 *
 * (user 2026-11-01: "referral link is missing like for the referral to refer to customer"
 *  / "so agent can share the link to them")
 *
 * NOT the same thing as "Send login", and the wording says so. That one is a credential
 * and must reach one person; this one is meant to be forwarded — it is how a referrer
 * sends customers in without ever signing in, which is what most of them will do.
 *
 * The code is minted the first time anybody asks for it and never changes afterwards: it
 * is an identity, and regenerating it would orphan the introductions already made under
 * it.
 */

const FONT = "'Poppins', sans-serif";

export default function ReferrerShareLinkDialog({ open, onClose, loading, error, data }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(data?.url || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Blocked on an insecure origin and in some in-app browsers; the field is
            // selectable, so there is still a way through.
            setCopied(false);
        }
    };

    const waHref = () => {
        const digits = String(data?.phone || '').replace(/\D/g, '');
        const intl = digits.startsWith('60') ? digits : '60' + digits.replace(/^0/, '');
        const msg =
            `Hi ${data?.name || ''}, this is your referral link.\n\n` +
            `${data?.url || ''}\n\n` +
            `Send it to anyone you think should go solar — whoever fills it in comes ` +
            `through as your referral, and you earn on it when they sign.`;
        return `https://wa.me/${intl}?text=${encodeURIComponent(msg)}`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Referral link</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#64748B', mb: 2 }}>
                            {data?.name || 'They'} can send this to anyone. Whoever fills it in arrives as
                            their referral, and they earn on it when that person signs — no login needed
                            at their end. The link does not expire and stays the same, so it is safe to
                            forward.
                        </Typography>

                        <TextField
                            fullWidth size="small" value={data?.url || ''}
                            InputProps={{ readOnly: true }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem', fontFamily: FONT } }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ fontFamily: FONT, textTransform: 'none' }}>Close</Button>
                <Button
                    onClick={copy}
                    disabled={!data?.url}
                    variant="outlined"
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600 }}
                >
                    {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Button
                    component="a"
                    href={data?.phone ? waHref() : undefined}
                    target="_blank"
                    rel="noreferrer"
                    disabled={!data?.url || !data?.phone}
                    variant="contained"
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600 }}
                >
                    Send on WhatsApp
                </Button>
            </DialogActions>
        </Dialog>
    );
}
